import React, { useEffect, useRef, useState } from 'react';
import { FormComponent, UiComponent } from 'components';
import { useCheck, useDate, useFileService } from 'hooks';
import dayjs from 'dayjs';
import {
  ApplicationBaseType,
  ApplicationType,
  CAType,
  EventCreateType,
  buildApplicationTypeKeys,
  getApplicationBaseOrder,
  getApplicationBaseType,
  getApplicationTypeLabel,
  getApplicationTypeSeq,
  IEvent,
  IMapPosition,
  IOrganization,
  RecruitmentType,
} from 'types';
import { useNavigate } from 'react-router-dom';
import { EventQuery, FileServiceQuery } from 'queries';
import useModal from 'hooks/useModal';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import SelectOrganizationModal from './SelectOrganizationModal';
import TicketFormModal from './TicketFormModal';

const CHECK_LIST: {
  id: string;
  displayValue: string;
}[] = [
  { id: 'VMS', displayValue: 'VMS' },
  { id: '1365', displayValue: '1365' },
];

interface TicketRow {
  base: ApplicationBaseType;
  /** 이미 저장된 행의 키. 잠긴 뒤에는 이 값을 그대로 쓴다. */
  savedKey: ApplicationType | null;
  value: number;
  title: string;
}

/**
 * 티켓 행 정렬. 유형 선택 목록과 같은 순서를 1순위로 쓴다.
 *
 * 어드민이 붙인 순서는 저장되지 않는다 — 정원이 `{키: 인원}` 객체라
 * DB 가 키 순서를 정규화해버린다. 그래서 순서를 저장하는 대신 고정 규칙으로 되살리고,
 * 사용자 신청 화면도 같은 규칙을 쓴다.
 *
 * 새로 추가한 행(savedKey 없음)은 같은 유형의 기존 행 뒤에 붙는다.
 * 그래야 저장 시 발급되는 번호가 기존 행의 번호를 밀지 않는다.
 */
const compareTicketRows = (a: TicketRow, b: TicketRow) => {
  const order =
    getApplicationBaseOrder(a.base) - getApplicationBaseOrder(b.base);
  if (order !== 0) return order;

  if (a.savedKey && b.savedKey) {
    return (
      getApplicationTypeSeq(a.savedKey) - getApplicationTypeSeq(b.savedKey)
    );
  }
  if (!a.savedKey && b.savedKey) return 1;
  if (a.savedKey && !b.savedKey) return -1;

  return 0;
};

interface Props {
  event?: IEvent | null;
}

function Form({ event }: Props) {
  const { handleAlert, handleModal } = useModal();
  const navigate = useNavigate();
  const mapRef = useRef<{
    getData: () => { _lng: number; _lat: number; mapMarker?: IMapPosition };
    setData: () => void;
  }>(null);
  const { handleUploadd: upload } = FileServiceQuery.useUpload();
  const { mutate: create } = EventQuery.useCreate();
  const { mutate: update } = EventQuery.useUpdate();

  const { file, fileUrl, handleFile } = useFileService({
    initFileUrl: event?.mainImageUrl,
  });

  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const { data: currentOrgs } = EventQuery.useGetOrganizations(event?.id || 0);
  const { mutate: putOrganizations } = EventQuery.usePutOrganizations();

  useEffect(() => {
    if (currentOrgs) setOrganizations(currentOrgs);
  }, [currentOrgs]);
  // const [thumbnail, setThumbnail] = useState(event?.mainImageUrl || '0');
  const [title, setTitle] = useState(event?.title || '');
  const [participantCount, setParticipantCount] = useState(
    event?.participantCount || '',
  );
  const [locationName, setLocationName] = useState(event?.locationName || '');
  const [gatheringPlace, setGatheringPlace] = useState(
    event?.gatheringPlace || '',
  );

  const [volunteerHours, setVolunteerHours] = useState(
    event?.volunteerHours || '4',
  );

  // 모집 방식: 선착순(FCFS, 기본) | 추첨형(LOTTERY)
  const [recruitmentType, setRecruitmentType] = useState<RecruitmentType>(
    event?.recruitmentType || 'FCFS',
  );

  const [applicationType, setApplicationType] = useState(
    event?.applicationTypeQuota ? '2' : '1',
  );

  const [applicationTypes, setApplicationTypes] = useState<TicketRow[]>(
    event?.applicationTypeQuota
      ? Object.entries(event.applicationTypeQuota)
          .map(([key, value]) => ({
            base: getApplicationBaseType(key) as ApplicationBaseType,
            savedKey: key as ApplicationType,
            value: value as number,
            title: event.applicationTypeQuotaTitle?.[key] || '',
          }))
          .sort(compareTicketRows)
      : [],
  );

  const isQuotaModeLocked = !!event;

  /**
   * 티켓 유형 추가·삭제와 모집 방식(선착순 ↔ 추첨형) 변경은
   * 검토(REVIEW) 상태이고 모집 시작 전일 때만 가능하다.
   *
   * 모집이 시작된 뒤에 모집 방식을 바꾸면 이미 접수된 신청의 처리 방식
   * (선착순 즉시 확정 / 추첨 대기) 이 달라져버린다.
   */
  const isTicketLocked =
    !!event &&
    (event.eventStatus !== 'REVIEW' ||
      dayjs().isAfter(dayjs(event.enrollStartDate)));

  /**
   * 저장할 키.
   * 잠긴 뒤에는 재계산하지 않는다 — 접미사 규칙이 다른 과거 데이터
   * (예: DRIVING_2 만 남아 있는 경우) 가 다른 키로 바뀌어버린다.
   */
  const typeKeys: ApplicationType[] = isTicketLocked
    ? applicationTypes.map((row) => row.savedKey ?? row.base)
    : buildApplicationTypeKeys(applicationTypes.map((row) => row.base));

  const baseCounts = applicationTypes.reduce<Record<string, number>>(
    (acc, row) => {
      acc[row.base] = (acc[row.base] ?? 0) + 1;
      return acc;
    },
    {},
  );

  /**
   * 같은 의미의 유형이 둘 이상인데 티켓 명이 비어 있는 행.
   * 신청 화면에서 둘 다 '운전' 으로 보여 사용자가 무엇을 고를지 알 수 없다.
   * 두 번째를 추가하면 첫 번째 행도 같이 이름이 필요해지므로 모달 검증만으로는 부족하다.
   */
  const needsTitle = (row: { base: ApplicationBaseType; title: string }) =>
    (baseCounts[row.base] ?? 0) > 1 && !row.title.trim();

  const dateState = useDate({
    defaultStart: event?.startDate,
    defaultEnd: event?.endDate,
  });
  const enlistDateState = useDate({
    defaultStart: event?.enrollStartDate,
    defaultEnd: event?.enrollEndDate,
  });

  const checkListProps = useCheck({
    uniqueKey: 'id',
    data: CHECK_LIST,
    defaultChecks: event?.certificationAgency || ['VMS', '1365'],
  });

  const isLottery = recruitmentType === 'LOTTERY';

  const handleSubmit = async () => {
    if (!mapRef.current) return;
    const isEdit = !!event;

    const { _lat, _lng, mapMarker } = mapRef.current.getData();

    // 선착순(단일)일 때만 정원 필수. 추첨형은 신청 단계 정원 제한이 없음.
    if (
      !isLottery &&
      applicationType === '1' &&
      (!participantCount || isNaN(Number(participantCount)))
    )
      return;

    // 다중 티켓 검증은 선착순에서만 유효 (추첨형은 단일 정원으로 고정)
    if (!isLottery && applicationType === '2') {
      if (applicationTypes.length < 2) {
        toast.error('다중 티켓은 2개 이상 설정해야 합니다.');
        return;
      }
      if (applicationTypes.some((v) => v.value <= 0)) {
        toast.error('모든 티켓의 인원은 1 이상이어야 합니다.');
        return;
      }
      // 행에 인라인으로 이미 표시되고 있지만, 마지막 방어선으로 저장도 막는다.
      if (applicationTypes.some(needsTitle)) {
        toast.error(
          '같은 유형을 여러 개 등록할 때는 티켓 명을 입력해야 합니다.',
        );
        return;
      }
    }

    if (!fileUrl) {
      handleAlert('대표이미지를 등록해주세요');
      return;
    }

    if (organizations.length === 0) {
      handleAlert('조직을 선택해주세요');
      return;
    }

    let mainImageUrl = fileUrl;
    let isNewFile = false;
    if (file) {
      const res = await upload(file);
      if (!res) return;
      mainImageUrl = res.data.url;
      isNewFile = true;
    }

    const selectedOrgName =
      organizations.length === 1
        ? organizations[0].displayName
        : organizations.map((o) => o.displayName).join(' · ') ||
          event?.organizationName ||
          '';

    // 추첨형은 단일 정원만 지원하므로 유형별 쿼터를 쓰지 않음
    const useTypeQuota = !isLottery && applicationType === '2';

    const formData: EventCreateType = {
      organizationIds: organizations.map((o) => o.id),
      title,
      startDate: dayjs(dateState.start).format('YYYY-MM-DDTHH:mm:ss'),
      endDate: dayjs(dateState.end).format('YYYY-MM-DDTHH:mm:ss'),
      enrollStartDate: dayjs(enlistDateState.start).format(
        'YYYY-MM-DDTHH:mm:ss',
      ),
      enrollEndDate: dayjs(enlistDateState.end).format('YYYY-MM-DDTHH:mm:ss'),
      certificationAgency: checkListProps.idData
        .filter((v) => v.checked)
        .map((v) => v.displayValue as CAType),
      locationName,
      gatheringPlace,
      latitude: _lat,
      longitude: _lng,
      // 추첨형은 신청 단계 정원 제한이 없어 0으로 저장
      participantCount: useTypeQuota
        ? 0
        : isLottery
          ? 0
          : Number(participantCount),
      recruitmentType,
      organizationName: selectedOrgName || '연봉인상',
      mainImageUrl,
      volunteerHours: Number(volunteerHours),
      applicationTypeQuota: useTypeQuota
        ? applicationTypes.reduce(
            (acc, curr, i) => {
              acc[typeKeys[i]] = curr.value;
              return acc;
            },
            {} as Record<ApplicationType, number>,
          )
        : null,
      applicationTypeQuotaTitle: useTypeQuota
        ? applicationTypes.reduce(
            (acc, curr, i) => {
              acc[typeKeys[i]] = curr.title;
              return acc;
            },
            {} as Record<string, string>,
          )
        : null,
    };

    if (mapMarker) {
      formData.centerLatitude = mapMarker._lat;
      formData.centerLongitude = mapMarker._lng;
    }

    if (!isEdit) {
      formData.description = '';
      create(formData, {
        onSuccess: () => {
          toast.success('행사 등록이 완료되었습니다.');
          navigate(-1);
        },
        onError: () => {
          toast.error(DEFAULT_ERROR_MESSAGE);
        },
      });
    } else if (event) {
      const { organizationIds: _, ...updateFormData } = formData;
      update(
        { ...updateFormData, id: event.id, isNewFile },
        {
          onSuccess: () => {
            const sortOrder = organizations.map((o, i) => ({
              organizationId: o.id,
              sortOrder: i,
            }));
            putOrganizations(
              {
                id: event.id,
                organizationIds: organizations.map((o) => o.id),
                organizationSortOrder: sortOrder,
              },
              {
                onSuccess: () => {
                  toast.success('행사 수정이 완료되었습니다.');
                  navigate(-1);
                },
                onError: () => {
                  toast.error(DEFAULT_ERROR_MESSAGE);
                },
              },
            );
          },
          onError: () => {
            toast.error(DEFAULT_ERROR_MESSAGE);
          },
        },
      );
    }
  };

  return (
    <div css={{ padding: '16px 0' }}>
      <h2 css={{ marginBottom: '24px', fontSize: '18px' }}>행사 등록</h2>
      <UiComponent.Box>
        <div
          css={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <b>기본 정보</b>
          <UiComponent.Button
            variant="contained"
            color="primary"
            type="submit"
            css={{ width: '120px' }}
            onClick={handleSubmit}
          >
            {event ? '수정' : '등록'}
          </UiComponent.Button>
        </div>
        <div
          css={{
            display: 'flex',
            gap: '40px',
            padding: '16px 0',
            alignItems: 'flex-start',
          }}
        >
          {/* 대표 이미지 */}
          <div>
            <p css={{ fontWeight: '600', marginBottom: '8px' }}>대표 이미지</p>
            <input type="file" name="thumbnail" onChange={handleFile} />
            {fileUrl && (
              <div
                css={{
                  marginTop: '8px',
                  width: '200px',
                  height: '200px',
                  userSelect: 'none',
                }}
              >
                <img
                  src={fileUrl}
                  alt="thumbnail"
                  width={'100%'}
                  css={{ objectFit: 'cover' }}
                />
              </div>
            )}
          </div>
          {/* 조직 */}
          <div>
            <p css={{ fontWeight: '600', marginBottom: '8px' }}>단체</p>
            <div css={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <UiComponent.Button
                variant="contained"
                color="primary"
                onClick={() =>
                  handleModal(
                    '단체 선택',
                    <SelectOrganizationModal
                      organizations={organizations}
                      onSubmit={setOrganizations}
                    />,
                    <></>,
                  )
                }
              >
                단체 선택
              </UiComponent.Button>
              {organizations.length > 0 && (
                <span css={{ fontSize: '14px', color: '#333' }}>
                  {organizations.map((o) => o.displayName).join(' · ')}
                </span>
              )}
            </div>
          </div>
        </div>
        {/* <EventComponent.ThumbnailSelector
          thumbnail={thumbnail}
          setThumbnail={setThumbnail}
        /> */}
        <div css={{ display: 'flex', gap: '0 32px', marginTop: '24px' }}>
          <FormComponent.Form
            submit={handleSubmit}
            css={{
              '> input': { width: '100%' },
              '> textarea': { width: '100%' },
              '> p': { margin: '16px 0', fontWeight: '600' },
            }}
          >
            <p>행사명</p>
            <FormComponent.Input
              name="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <p>일시</p>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                '> div': { width: 'auto' },
              }}
            >
              <DatePicker
                selected={dateState.start}
                onChange={(date) => date && dateState.handleStart(date)}
                showTimeSelect
                dateFormat="yyyy.MM.dd HH:mm"
              />
              <p css={{ margin: '0 4px' }}>~</p>
              <DatePicker
                selected={dateState.end}
                onChange={(date) => date && dateState.handleEnd(date)}
                showTimeSelect
                dateFormat="yyyy.MM.dd HH:mm"
              />
            </div>

            <p>모집 기간</p>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                '> div': { width: 'auto' },
              }}
            >
              <DatePicker
                selected={enlistDateState.start}
                onChange={(date) => date && enlistDateState.handleStart(date)}
                showTimeSelect
                dateFormat="yyyy.MM.dd HH:mm"
              />
              <p css={{ margin: '0 4px' }}>~</p>
              <DatePicker
                selected={enlistDateState.end}
                onChange={(date) => date && enlistDateState.handleEnd(date)}
                showTimeSelect
                dateFormat="yyyy.MM.dd HH:mm"
              />
            </div>

            <p>인증 기관</p>
            <div css={{ display: 'flex', gap: '0 24px' }}>
              <FormComponent.CheckList {...checkListProps} uniqueKey="id" />
            </div>

            <p>봉사시간</p>
            <FormComponent.Input
              type="number"
              value={String(Number(volunteerHours))}
              onChange={(e) => setVolunteerHours(e.target.value)}
            />

            <p>모집 방식</p>
            {/* 모집이 시작된 뒤에는 바꿀 수 없으니 고를 수 없는 라디오를 남겨두지 않고
                현재 값만 텍스트로 보여준다. */}
            {isTicketLocked ? (
              <p css={{ marginBottom: '8px' }}>
                {isLottery ? '추첨형' : '선착순'}
              </p>
            ) : (
              <div
                css={{
                  display: 'flex',
                  gap: '0 16px',
                  marginBottom: '8px',
                  '> label': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <label>
                  <FormComponent.CheckRadio
                    name="recruitmentType"
                    value="FCFS"
                    checked={recruitmentType === 'FCFS'}
                    handleCheck={(checked) =>
                      setRecruitmentType(checked ? 'FCFS' : 'LOTTERY')
                    }
                  />
                  선착순
                </label>
                <label>
                  <FormComponent.CheckRadio
                    name="recruitmentType"
                    value="LOTTERY"
                    checked={recruitmentType === 'LOTTERY'}
                    handleCheck={(checked) => {
                      const next: RecruitmentType = checked
                        ? 'LOTTERY'
                        : 'FCFS';
                      setRecruitmentType(next);
                      // 추첨형은 단일 정원만 지원 (유형별 추첨 미지원)
                      if (next === 'LOTTERY') setApplicationType('1');
                    }}
                  />
                  추첨형
                </label>
              </div>
            )}

            {!isLottery && (
              <>
                <p>인원</p>
                {/* 등록 이후에는 바꿀 수 없으니 고를 수 없는 라디오를 남겨두지 않고
                    현재 값만 텍스트로 보여준다. */}
                {isQuotaModeLocked ? (
                  <p css={{ marginBottom: '16px' }}>
                    {`티켓 유형 - ${applicationType === '2' ? '다중' : '단일'}`}
                  </p>
                ) : (
                  <div
                    css={{
                      display: 'flex',
                      gap: '0 16px',
                      marginBottom: '16px',
                      '> label': {
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  >
                    <label>
                      <FormComponent.CheckRadio
                        name="applicationType"
                        value="1"
                        checked={applicationType === '1'}
                        handleCheck={(checked) =>
                          setApplicationType(checked ? '1' : '2')
                        }
                      />
                      단일
                    </label>
                    <label>
                      <FormComponent.CheckRadio
                        name="applicationType"
                        value="2"
                        checked={applicationType === '2'}
                        handleCheck={(checked) =>
                          setApplicationType(checked ? '2' : '1')
                        }
                      />
                      다중
                    </label>
                  </div>
                )}
                {applicationType === '1' && (
                  <FormComponent.Input
                    type="number"
                    value={participantCount}
                    onChange={(e) => setParticipantCount(e.target.value)}
                  />
                )}
                {applicationType === '2' && (
                  <div
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {applicationTypes.map((v, i) => (
                      <div
                        key={`APPLICATION_TYPE_${i}`}
                        css={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '8px',
                          alignItems: 'center',
                        }}
                      >
                        <p
                          css={{
                            width: '100px',
                            minWidth: '100px',
                            fontWeight: 600,
                          }}
                        >
                          {getApplicationTypeLabel(v.base)}
                        </p>
                        <FormComponent.Input
                          placeholder={
                            needsTitle(v) ? '티켓 명 (필수)' : '티켓 명'
                          }
                          value={v.title}
                          css={
                            needsTitle(v)
                              ? { borderColor: '#e5484d' }
                              : undefined
                          }
                          onChange={(e) => {
                            setApplicationTypes(
                              applicationTypes.map((v, j) =>
                                j === i ? { ...v, title: e.target.value } : v,
                              ),
                            );
                          }}
                        />
                        <FormComponent.Input
                          type="number"
                          value={v.value}
                          max={99}
                          css={{ width: '60px', minWidth: '60px' }}
                          onChange={(e) => {
                            setApplicationTypes(
                              applicationTypes.map((v, j) =>
                                j === i
                                  ? { ...v, value: Number(e.target.value) }
                                  : v,
                              ),
                            );
                          }}
                        />
                        {!isTicketLocked && (
                          <UiComponent.Button
                            variant="border"
                            color="primary"
                            onClick={() =>
                              setApplicationTypes((prev) =>
                                prev.filter((_, j) => j !== i),
                              )
                            }
                          >
                            삭제
                          </UiComponent.Button>
                        )}
                      </div>
                    ))}
                    {/* 잠긴 뒤에는 삭제 버튼과 마찬가지로 아예 감춘다.
                        누를 수 없는 버튼을 남겨두는 것보다 안내 문구 한 줄이 낫다. */}
                    {!isTicketLocked && (
                      <div css={{ display: 'flex', gap: '0 4px' }}>
                        <UiComponent.Button
                          variant="contained"
                          color="primary"
                          onClick={() =>
                            handleModal(
                              '티켓 유형 추가',
                              <TicketFormModal
                                existingBases={applicationTypes.map(
                                  (row) => row.base,
                                )}
                                onSubmit={(draft) =>
                                  setApplicationTypes((prev) =>
                                    [
                                      ...prev,
                                      { ...draft, savedKey: null },
                                    ].sort(compareTicketRows),
                                  )
                                }
                              />,
                              <></>,
                            )
                          }
                        >
                          추가
                        </UiComponent.Button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </FormComponent.Form>
          <div>
            <p
              css={{
                margin: '16px 0',
                fontWeight: '600',
              }}
            >
              장소
            </p>
            <FormComponent.SearchMap
              ref={mapRef}
              latitude={event?.latitude}
              longitude={event?.longitude}
              // mapMarker={}
            />
            <p
              css={{
                margin: '16px 0',
                fontWeight: '600',
              }}
            >
              행사 장소
            </p>
            <FormComponent.Input
              css={{ width: '500px' }}
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
            />

            <p
              css={{
                margin: '16px 0',
                fontWeight: '600',
              }}
            >
              집결 장소
            </p>
            <FormComponent.Input
              css={{ width: '500px' }}
              value={gatheringPlace}
              onChange={(e) => setGatheringPlace(e.target.value)}
            />
          </div>
        </div>
      </UiComponent.Box>
    </div>
  );
}

export default Form;
